import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useHub } from "../../../core/Global"
import Indicator from "../../../components/base/Indicator"
import Icon from "../../../components/base/Icon"
import { toastError, toastSuccess } from "../../../components/base/Toast"
import { BillingDetails, OfferView as Offer } from "../../enterprise/models/Enterprise"
import { toCount, toDate, toMoney, toRate, toUnit } from "../../plancatalog/components/format"
import TextBox from "../../../components/form/TextBox"
import TextArea from "../../../components/form/TextArea"
import Form from "../../../components/form/Form"
import HUBResponse from "../../../models/HUBResponse"
import { Validator } from "../../../core/Types"
import "./OfferView.css"


type Step = 1 | 2 | 3 | 4

// Checked in the browser with the console's own form validation, which writes each message
// beside the field's label; the hub checks the same again and answers with the field's name.
const DetailsValidator: Validator[] = [
    { name: "legalName", required: true, maxLength: 200 },
    { name: "addressLine1", required: true, maxLength: 200 },
    { name: "addressLine2", maxLength: 200 },
    { name: "city", required: true, maxLength: 100 },
    { name: "country", required: true, maxLength: 100 },
    { name: "billingContactName", required: true, maxLength: 100 },
    { name: "billingContactEmail", required: true, type: "Email" }
]

const SignValidator: Validator[] = [
    { name: "signName", required: true, minLength: 3, maxLength: 100 },
    { name: "signTitle", required: true, maxLength: 100 },
    { name: "agreed", required: true }
]

const ChangeValidator: Validator[] = [
    { name: "changeText", required: true, minLength: 5, maxLength: 4000 }
]

const CodeValidator: Validator[] = [
    { name: "code", required: true, minLength: 6, maxLength: 6 }
]

const query = new URLSearchParams(window.location.search)


// The customer's Enterprise offer (spec §1.6, A8): review it, ask for a change, give the
// company's details, sign, then create the account and pay -- or, for a customer who already
// has an account, switch it over. The figures are read-only here; every opening is logged.
export default function OfferView() {

    const { token } = useParams()
    const hub = useHub()

    const [offer, offerSet] = useState<Offer>()
    const [error, errorSet] = useState<string>()
    const [step, stepSet] = useState<Step>(1)
    const [working, workingSet] = useState<boolean>(false)

    const [changeOpen, changeOpenSet] = useState<boolean>(false)
    const [change, changeSet] = useState<{ name: string, text: string }>({ name: "", text: "" })

    const [details, detailsSet] = useState<BillingDetails>({})
    const [sign, signSet] = useState<{ name: string, title: string, agreed: boolean }>({ name: "", title: "", agreed: false })

    const [verify, verifySet] = useState<{ requestId: string, signupToken: string }>()
    const [code, codeSet] = useState<string>("")

    const load = (moveOn: boolean = true) => {
        hub.Get("/auth/offer/" + token, res => {
            if (!res.success) return errorSet(res.message ?? "This offer link is not valid.")
            const o: Offer = res.data
            offerSet(o)
            if (o.billingDetails) detailsSet(o.billingDetails)
            if (moveOn) stepSet(o.signed ? 4 : o.billingDetails ? 3 : 1)
        })
    }

    useEffect(() => {
        load()
        if (query.get("payment") === "failed") toastError("The payment did not go through. Please try again.")
        if (query.get("payment") === "canceled") toastError("The payment was cancelled.")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (error) return <Closed text={error} />
    if (!offer) return <div className="py-20"><Indicator /></div>
    if (offer.closedReason) return <Closed text={offer.closedReason} />
    if (offer.activated) return <Done offer={offer} />

    // A form's callback gets the answer, so an error the hub ties to a field is shown beside
    // that field; anything else is a toast.
    const post = (path: string, body: any, ok: (data: any) => void, callback?: (res?: HUBResponse<any>) => void) => {
        workingSet(true)
        hub.Post(`/auth/offer/${token}/${path}`, body, res => {
            workingSet(false)
            callback?.(res)
            if (!res.success) {
                if (!res.field) toastError(res.message ?? "Something went wrong.")
                return
            }
            ok(res.data)
        }, () => { workingSet(false); callback?.({ success: false } as any) })
    }

    const sendChange = (callback: any) => post("change", change, () => {
        toastSuccess("Thank you — your representative will come back to you.")
        changeOpenSet(false)
        changeSet({ name: "", text: "" })
        load(false)
    }, callback)

    const saveDetails = (callback: any) => post("details", details, () => { load(false); stepSet(3) }, callback)

    const signIt = (callback: any) => post("sign", sign, () => { toastSuccess("Signed. Thank you."); load() }, callback)

    const startAccount = () => post("account", {}, data => {
        verifySet({ requestId: data.requestId, signupToken: data.signupToken })
        toastSuccess(`A code is on its way to ${offer.contactEmail}.`)
    })

    const checkCode = (callback: any) => {
        if (!verify) return
        workingSet(true)
        hub.Post(`/auth/signup/verify/${verify.requestId}`, { code }, res => {
            workingSet(false)
            callback?.(res)
            if (!res.success) {
                if (res.message === "codeexceed") toastError("Too many tries. Please ask for a new code.")
                else if (!res.field) toastError(res.message ?? "That code is not right.")
                return
            }
            document.location.href = "/join/" + verify.signupToken
        }, () => { workingSet(false); callback?.({ success: false }) })
    }

    const activate = () => post("activate", {}, data => {
        if (data?.url) document.location.href = data.url
        else load()
    })

    const steps: [Step, string][] = [[1, "Your offer"], [2, "Company & billing"], [3, "Review & sign"], [4, offer.forExistingAccount ? "Activate" : "Your account"]]
    const reachable = (s: Step) => s === 1 || (s === 2 && !offer.signed) || (s === 3 && !!offer.billingDetails) || (s === 4 && !!offer.signed)

    return (
        <div className="offer-page">

            <div className="offer-head">
                <div>
                    <div className="offer-eyebrow">Rinvox Enterprise · {offer.reference}</div>
                    <h1>Offer for {offer.companyName}</h1>
                    <div className="text-gray-600">Prepared for {offer.contactName} · open until {toDate(offer.validUntil)}</div>
                </div>
                <div className="offer-head-price">
                    <b>{toMoney(offer.monthly)}</b><span>/ month</span>
                    {offer.oneTime > 0 && <div>+ {toMoney(offer.oneTime)} one-time</div>}
                </div>
            </div>

            <div className="offer-steps">
                {
                    steps.map(([s, label]) =>
                        <button key={s} type="button" disabled={!reachable(s)}
                            className={"offer-step" + (step === s ? " is-active" : "") + (s < step || (s === 3 && offer.signed) ? " is-done" : "")}
                            onClick={() => reachable(s) && stepSet(s)}>
                            <span>{s < step || (s === 3 && offer.signed) ? <Icon icon="check" type="solid" size={10} /> : s}</span>
                            {label}
                        </button>
                    )
                }
            </div>

            {/* ------------------------------------------------ 1. the offer */}
            {
                step === 1 &&
                <div className="offer-card">
                    <table className="offer-lines">
                        <thead><tr><th>Item</th><th>Quantity</th><th>Unit price</th><th>Total</th></tr></thead>
                        <tbody>
                            {
                                offer.lines.map((l, i) =>
                                    <tr key={i}>
                                        <td>{l.label}</td>
                                        <td>{toCount(l.quantity)}</td>
                                        <td>{toUnit(l.unitPrice)}{l.oneTime ? "" : ` / ${l.unit ?? "month"}`}</td>
                                        <td>{toMoney(l.total)}{l.oneTime ? " once" : " / mo"}</td>
                                    </tr>
                                )
                            }
                        </tbody>
                        <tfoot>
                            {/* One cell per column, so the unit-price column can be hidden on a phone. */}
                            <tr><td>Monthly</td><td /><td /><td>{toMoney(offer.monthly)}</td></tr>
                            {offer.billing === "annual" && <tr><td>Billed yearly</td><td /><td /><td>{toMoney(offer.periodPrice)} / yr</td></tr>}
                            {offer.oneTime > 0 && <tr><td>One-time</td><td /><td /><td>{toMoney(offer.oneTime)}</td></tr>}
                        </tfoot>
                    </table>

                    <div className="offer-facts">
                        <Fact label="Included every month" value={`${toCount(offer.includedUsers)} users at once · ${toCount(offer.includedMessages)} messages · ${toCount(offer.includedMinutes)} call minutes · ${toCount(offer.includedNumbers)} phone numbers`} />
                        <Fact label="Beyond the allowance" value={`${toRate(offer.overageMessageRate)} a message · ${toRate(offer.overageMinuteRate)} a minute · ${toMoney(offer.extraNumberPrice)} per extra number · ${toMoney(offer.extraUserPrice)} per extra user, a month`} />
                        <Fact label="Also included" value={[offer.whatsAppIncluded && "WhatsApp", offer.telegramIncluded && "Telegram", offer.extensionsSupportIncluded && "Masked Job Calls", offer.callRecordingIncluded && `Call recording, kept ${offer.recordingRetentionMonths} months`].filter(Boolean).join(" · ") || "—"} />
                        <Fact label="Support" value={offer.supportTier || "—"} />
                        <Fact label="Term" value={`${offer.termMonths} months` + (offer.autoRenew ? `, renewing automatically with a ${offer.renewalUpliftPercent}% increase` : "")} />
                        <Fact label="Payment" value="By card, charged in advance" />
                    </div>

                    {offer.customerNote && <div className="offer-note">{offer.customerNote}</div>}

                    {
                        changeOpen ?
                            <Form className="offer-change" validator={ChangeValidator} onSubmit={(body, callback) => sendChange(callback)}>
                                <TextBox id="offer-change-name" name="changeName" label="Your name" labelUp block marginBottom={3}
                                    value={change.name} onTextChanges={v => changeSet({ ...change, name: v })} />
                                <TextArea id="offer-change-text" name="changeText" label="What would you like changed? *" labelUp rows={4} block marginBottom={4}
                                    placeholder="For example: 40 users instead of 30"
                                    value={change.text} onChange={e => changeSet({ ...change, text: e.target.value })} />
                                <div className="d-flex gap-2">
                                    <button type="button" className="btn btn-light" onClick={() => changeOpenSet(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={working}>Send request</button>
                                </div>
                            </Form>
                            :
                            <div className="offer-actions">
                                {
                                    !offer.signed &&
                                    <button type="button" className="btn btn-light" onClick={() => changeOpenSet(true)}>
                                        <Icon icon="pen" size={12} className="me-2" />Request a change
                                    </button>
                                }
                                <button type="button" className="btn btn-primary ms-auto" onClick={() => stepSet(offer.signed ? 4 : 2)}>
                                    Continue<Icon icon="chevron-right" size={12} className="ms-2" />
                                </button>
                            </div>
                    }
                    {offer.changeRequests > 0 && !changeOpen && <div className="text-gray-500 fs-8 mt-3">You have sent {offer.changeRequests} change request{offer.changeRequests === 1 ? "" : "s"}. A revised offer replaces this one.</div>}
                </div>
            }

            {/* ------------------------------------------------ 2. company and billing */}
            {
                step === 2 &&
                <Form className="offer-card" validator={DetailsValidator} onSubmit={(body, callback) => saveDetails(callback)}>
                    <div className="offer-grid">
                        <Input name="legalName" label="Legal company name *" value={details.legalName} onChange={v => detailsSet({ ...details, legalName: v })} wide />
                        <Input name="addressLine1" label="Address *" value={details.addressLine1} onChange={v => detailsSet({ ...details, addressLine1: v })} wide />
                        <Input name="addressLine2" label="Address line 2" value={details.addressLine2} onChange={v => detailsSet({ ...details, addressLine2: v })} wide />
                        <Input name="city" label="City *" value={details.city} onChange={v => detailsSet({ ...details, city: v })} />
                        <Input name="state" label="State / region" value={details.state} onChange={v => detailsSet({ ...details, state: v })} />
                        <Input name="postalCode" label="Postal code" value={details.postalCode} onChange={v => detailsSet({ ...details, postalCode: v })} />
                        <Input name="country" label="Country *" value={details.country} onChange={v => detailsSet({ ...details, country: v })} />
                        <Input name="taxId" label="Tax ID" value={details.taxId} onChange={v => detailsSet({ ...details, taxId: v })} />
                        <Input name="poNumber" label="Purchase order number" value={details.poNumber} onChange={v => detailsSet({ ...details, poNumber: v })} />
                        <Input name="billingContactName" label="Billing contact *" value={details.billingContactName} onChange={v => detailsSet({ ...details, billingContactName: v })} />
                        <Input name="billingContactEmail" label="Billing contact email *" email value={details.billingContactEmail} onChange={v => detailsSet({ ...details, billingContactEmail: v })} />
                    </div>
                    <div className="offer-actions">
                        <button type="button" className="btn btn-light" onClick={() => stepSet(1)}>Back</button>
                        <button type="submit" className="btn btn-primary ms-auto" disabled={working}>
                            Save &amp; continue<Icon icon="chevron-right" size={12} className="ms-2" />
                        </button>
                    </div>
                </Form>
            }

            {/* ------------------------------------------------ 3. sign */}
            {
                step === 3 &&
                <div className="offer-card">
                    {
                        offer.documents.map(d =>
                            <div key={d.key} className="offer-doc">
                                <div className="offer-doc-title"><Icon icon="file-lines" size={12} className="me-2" />{d.title}</div>
                                <pre>{d.text}</pre>
                            </div>
                        )
                    }

                    {
                        offer.signed ?
                            <div className="offer-signed">
                                <Icon icon="file-signature" size={16} />
                                Signed by {offer.signedName} on {toDate(offer.signedAt)}.
                            </div>
                            :
                            <Form validator={SignValidator} onSubmit={(body, callback) => signIt(callback)}>
                                <div className="offer-grid mt-6">
                                    <Input name="signName" label="Your full name *" value={sign.name} onChange={v => signSet({ ...sign, name: v })} />
                                    <Input name="signTitle" label="Your title *" value={sign.title} onChange={v => signSet({ ...sign, title: v })} />
                                </div>
                                <label className="offer-agree">
                                    <input type="checkbox" name="agreed" value="true" checked={sign.agreed}
                                        onChange={e => { window.hideFormError("agreed"); signSet({ ...sign, agreed: e.target.checked }) }} />
                                    <span>
                                        I have read the Service Agreement, the Data Processing Agreement and the Order Form above, and I agree
                                        to them on behalf of <b>{offer.billingDetails?.legalName ?? offer.companyName}</b>. Typing my name is my signature.
                                        <span id="agreed-err" className="small text-danger mx-3 form-error"></span>
                                    </span>
                                </label>
                                <div className="offer-actions">
                                    <button type="button" className="btn btn-light" onClick={() => stepSet(2)}>Back</button>
                                    <button type="submit" className="btn btn-primary ms-auto" disabled={working}>
                                        <Icon icon="file-signature" size={12} className="me-2" />Sign
                                    </button>
                                </div>
                            </Form>
                    }
                </div>
            }

            {/* ------------------------------------------------ 4. account and payment */}
            {
                step === 4 &&
                <div className="offer-card">
                    {
                        offer.forExistingAccount ?
                            <>
                                <h3 className="mb-2">Switch your account to Enterprise</h3>
                                <p className="text-gray-700">
                                    {
                                        offer.firstRenewalAt
                                            ? <>Your plan switches over now. The period you have already paid for runs on, and the Enterprise price is first charged on <b>{toDate(offer.firstRenewalAt)}</b>.</>
                                            : <>Your plan switches over as soon as the first payment goes through.</>
                                    }
                                </p>
                                <div className="offer-due">
                                    <span>Charged today</span>
                                    <b>{toMoney(offer.chargeToday)}</b>
                                </div>
                                <div className="offer-actions">
                                    <button type="button" className="btn btn-primary ms-auto" disabled={working} onClick={activate}>
                                        <Icon icon={offer.chargeToday > 0 ? "lock" : "circle-check"} size={12} className="me-2" />
                                        {offer.chargeToday > 0 ? `Pay ${toMoney(offer.chargeToday)} & activate` : "Activate Enterprise"}
                                    </button>
                                </div>
                            </>
                            :
                            <>
                                <h3 className="mb-2">Create your Rinvox account</h3>
                                <p className="text-gray-700">
                                    We'll send a verification code to <b>{offer.contactEmail}</b>. Then you choose a username and password,
                                    pay the first {offer.billing === "annual" ? "year" : "month"}{offer.oneTime > 0 ? " and the one-time charges" : ""} — <b>{toMoney(offer.chargeToday)}</b> by card —
                                    and set up your lines. We recommend turning on two-step sign-in for the account straight after.
                                </p>
                                {
                                    !verify ?
                                        <div className="offer-actions">
                                            <button type="button" className="btn btn-primary ms-auto" disabled={working} onClick={startAccount}>
                                                <Icon icon="envelope" size={12} className="me-2" />Send me the code
                                            </button>
                                        </div>
                                        :
                                        <Form className="offer-code" validator={CodeValidator} onSubmit={(body, callback) => checkCode(callback)}>
                                            <div className="offer-code-box">
                                                <TextBox id="offer-code" name="code" label="Verification code" labelUp numeric maxLength={6} placeholder="6-digit code" block noMarginBottom
                                                    value={code} onTextChanges={v => codeSet(v.replace(/\D/g, ""))} />
                                            </div>
                                            <button type="submit" className="btn btn-primary" disabled={working}>Continue</button>
                                            <button type="button" className="btn btn-link" disabled={working} onClick={startAccount}>Send again</button>
                                        </Form>
                                }
                            </>
                    }

                    <div className="offer-sms">
                        <Icon icon="comment-sms" size={14} />
                        <div>
                            <b>Business messaging registration</b> — {smsLabel(offer.smsRegistrationStatus)}
                            <div className="text-gray-600 fs-8">Carriers require every business to be registered before its text messages are delivered. Your account manager handles it with you once your account is set up.</div>
                        </div>
                    </div>
                </div>
            }

        </div>
    )
}


const smsLabel = (s?: string) =>
    s === "approved" ? "approved" : s === "submitted" ? "submitted to the carriers" : s === "rejected" ? "needs attention — your account manager will be in touch" : "not started yet"


function Closed(props: { text: string }) {
    return (
        <div className="offer-page">
            <div className="offer-card text-center py-15">
                <Icon icon="link-slash" type="thin" size={56} className="text-gray-400" />
                <div className="fs-4 fw-bold text-gray-800 mt-5">{props.text}</div>
            </div>
        </div>
    )
}

function Done(props: { offer: Offer }) {
    return (
        <div className="offer-page">
            <div className="offer-card text-center py-15">
                <Icon icon="circle-check" type="thin" size={56} className="text-success" />
                <div className="fs-3 fw-bolder text-gray-900 mt-5">Welcome to Rinvox Enterprise</div>
                <div className="text-gray-600 mt-2">Offer {props.offer.reference} is signed and your plan is running.</div>
                <div className="offer-sms text-start mt-8">
                    <Icon icon="comment-sms" size={14} />
                    <div>
                        <b>Business messaging registration</b> — {smsLabel(props.offer.smsRegistrationStatus)}
                        <div className="text-gray-600 fs-8">Your account manager will contact you to register your business with the carriers.</div>
                    </div>
                </div>
                <a href="/auth/login" className="btn btn-primary mt-8">Sign in to Rinvox</a>
            </div>
        </div>
    )
}

function Fact(props: { label: string, value: string }) {
    return (
        <div className="offer-fact">
            <span>{props.label}</span>
            <div>{props.value}</div>
        </div>
    )
}

// The console's own text box, so this page looks like the rest of Rinvox. The name is what
// the form validation and the hub's field errors find it by.
function Input(props: { name: string, label: string, value?: string, wide?: boolean, email?: boolean, onChange: (v: string) => void }) {
    return (
        <div className={"offer-field" + (props.wide ? " is-wide" : "")}>
            <TextBox id={"offer-" + props.name} name={props.name} label={props.label} labelUp block noMarginBottom email={props.email}
                value={props.value ?? ""} onTextChanges={props.onChange} />
        </div>
    )
}

